/**
 * Saturation Strike Event Tracker
 * ---------------------------------------------------------------
 * Captures tour engagement, conversion intent, URL identification,
 * and time-on-page metrics for the /lawn-strike route.
 *
 * High-value events (TOUR_COMPLETE, CONVERSION_INTENT) trigger a
 * Resend email briefing to John server-side.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const TRACK_ENDPOINT = `${API_BASE}/api/lawn-strike/track`;

let sessionStartTime = null;
let sessionId = null;

function generateSessionId() {
  return "ss_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
}

function getProspectIdentity() {
  const params = new URLSearchParams(window.location.search);
  return {
    company: params.get("company") || "unknown",
    primary_contact:
      params.get("primary_contact") || params.get("contact") || "unknown",
  };
}

/**
 * Core tracking function — logs to console AND fires to backend.
 * The backend decides email dispatch based on `send_briefing`.
 */
export function trackEvent(eventName, data = {}) {
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStartTime = Date.now();
  }

  const identity = getProspectIdentity();
  const timeOnPage = Math.round((Date.now() - sessionStartTime) / 1000);

  const payload = {
    event: eventName,
    session_id: sessionId,
    timestamp: new Date().toISOString(),
    time_on_page_seconds: timeOnPage,
    company: identity.company,
    primary_contact: identity.primary_contact,
    url: window.location.href,
    ...data,
  };

  // Always log to console
  // eslint-disable-next-line no-console
  console.log(`[SaturationStrike] ${eventName}`, payload);

  // Determine if this is a high-value event that needs email briefing
  const HIGH_VALUE = ["TOUR_COMPLETE", "CONVERSION_INTENT"];
  const isHighValue = HIGH_VALUE.includes(eventName);

  // Fire to backend — fire-and-forget so the UI never blocks
  fetch(TRACK_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...payload, send_briefing: isHighValue }),
    keepalive: true,
  }).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn("[SaturationStrike] Track send failed:", err);
  });
}

/**
 * Initialize page-level tracking. Call once on component mount.
 * Idempotent — safe under React 18 strict-mode double-invocation.
 */
export function initTracking() {
  // Reset session on every mount so each page-view is its own session.
  sessionId = generateSessionId();
  sessionStartTime = Date.now();
  trackEvent("PAGE_VIEW", { referrer: document.referrer });

  // Time milestones at 30s, 60s, 2min, 5min
  const timers = [30, 60, 120, 300].map((sec) =>
    setTimeout(() => trackEvent("TIME_MILESTONE", { seconds: sec }), sec * 1000)
  );

  const beforeUnload = () => {
    const totalTime = Math.round((Date.now() - sessionStartTime) / 1000);
    try {
      navigator.sendBeacon(
        TRACK_ENDPOINT,
        new Blob(
          [
            JSON.stringify({
              event: "PAGE_EXIT",
              session_id: sessionId,
              timestamp: new Date().toISOString(),
              time_on_page_seconds: totalTime,
              ...getProspectIdentity(),
              url: window.location.href,
              send_briefing: false,
            }),
          ],
          { type: "application/json" }
        )
      );
    } catch (_e) {
      /* sendBeacon best-effort; no-op on failure */
    }
  };
  window.addEventListener("beforeunload", beforeUnload);

  // Cleanup handle for callers that want to tear down the listeners
  // (returned so a React useEffect can clean up on unmount).
  return () => {
    timers.forEach((t) => clearTimeout(t));
    window.removeEventListener("beforeunload", beforeUnload);
  };
}
